import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { ArrowLeft, Loader2, Check, Sparkles, Settings2, Minimize2, Layout, Play } from 'lucide-react';
import { toast, Toaster } from 'sonner';
import { SENDAFooter } from '../components/SENDAFooter';
import { ModuleFeedback } from '../components/ModuleFeedback';
import { ReadOnlyBanner } from '../components/ReadOnlyBanner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Modulo55SistemaEjecucion() {
    const [vmv, setVmv] = useState('');
    const [entorno, setEntorno] = useState('');
    const [disparador, setDisparador] = useState('');
    const [secuencia, setSecuencia] = useState('');
    const [loading, setLoading] = useState(false);
    const [savingDraft, setSavingDraft] = useState(false);
    const [loadingDraft, setLoadingDraft] = useState(true);
    const [readOnly, setReadOnly] = useState(false);
    const [submittedAt, setSubmittedAt] = useState(null);
    const [submitted, setSubmitted] = useState(false);
    const [aiInsight, setAiInsight] = useState('');
    const [coherencia, setCoherencia] = useState('');

    useEffect(() => {
        (async () => {
            try {
                const { data } = await axios.get(`${API_URL}/api/user/draft/sistema_ejecucion`, { withCredentials: true });
                if (data.data) {
                    if (data.data.vmv) setVmv(data.data.vmv);
                    if (data.data.entorno) setEntorno(data.data.entorno);
                    if (data.data.disparador) setDisparador(data.data.disparador);
                    if (data.data.secuencia) setSecuencia(data.data.secuencia);
                }
                if (data.read_only) { setReadOnly(true); setSubmittedAt(data.submitted_at); }
            } catch {} finally { setLoadingDraft(false); }
        })();
    }, []);

    const saveDraft = useCallback(async () => {
        setSavingDraft(true);
        try { await axios.post(`${API_URL}/api/user/draft/save`, { module_id: 'sistema_ejecucion', data: { vmv, entorno, disparador, secuencia } }, { withCredentials: true }); } catch {} finally { setSavingDraft(false); }
    }, [vmv, entorno, disparador, secuencia]);

    useEffect(() => { if (loadingDraft || readOnly) return; const t = setTimeout(saveDraft, 2000); return () => clearTimeout(t); }, [vmv, entorno, disparador, secuencia, saveDraft, loadingDraft, readOnly]);

    const canSubmit = !readOnly && (vmv.trim().length > 0 && entorno.trim().length > 0 && disparador.trim().length > 0 && secuencia.trim().length > 0);

    const handleSubmit = async () => {
        if (readOnly) return;
        if (!canSubmit) return;
        setLoading(true);
        try {
            const { data } = await axios.post(`${API_URL}/api/user/modules/sistema-ejecucion/submit`, {
                vmv, entorno, disparador, secuencia
            }, { withCredentials: true });
            setAiInsight(data.ai_insight);
            setCoherencia(data.coherencia);
            setSubmitted(true);
            toast.success('Sistema de ejecución completado');
        } catch (error) { toast.error(error.response?.data?.detail || 'Error al enviar'); }
        finally { setLoading(false); }
    };

    if (loadingDraft) return <div className="min-h-screen bg-[#F5F7F8] flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#3E5F6E]" /></div>;

    if (submitted) return (
        <div className="min-h-screen bg-[#F5F7F8] flex flex-col"><Toaster position="top-right" richColors />
            <div className="flex-1 flex items-center justify-center p-4">
                <Card className="w-full max-w-xl bg-white border border-[#DCE3E8] rounded-2xl shadow-xl"><CardContent className="pt-8 text-center">
                    <div className="w-20 h-20 bg-gradient-to-br from-[#E8B27A] to-[#D4A06A] rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg"><Settings2 className="h-10 w-10 text-white" /></div>
                    <h2 className="text-2xl font-light text-[#1F2A30] font-['Outfit'] mb-2" data-testid="sistema-ejecucion-success-title">Sistema de ejecución integrado</h2>
                    <p className="text-[#E8B27A] font-medium mb-4">Tu sistema completo está listo para activarse</p>
                    {coherencia && <div className="inline-block px-4 py-1.5 bg-[#5FAF9D]/10 text-[#5FAF9D] rounded-full text-sm font-medium mb-4">Coherencia: {coherencia}</div>}
                    {aiInsight && <div className="bg-[#F5F7F8] border-l-4 border-[#E8B27A] p-4 rounded-r-xl text-left mb-6"><div className="flex items-start gap-3"><Sparkles className="h-5 w-5 text-[#E8B27A] shrink-0 mt-0.5" /><p className="text-[#1F2A30] italic text-sm">"{aiInsight}"</p></div></div>}
                    <div className="mb-6"><ModuleFeedback moduleId="sistema_ejecucion" chapter={5} /></div>
                    <Link to="/usuario"><Button className="bg-[#3E5F6E] hover:bg-[#2F4A57] text-white px-8 py-3 rounded-xl" data-testid="continue-after-sistema-ejecucion">Continuar</Button></Link>
                </CardContent></Card>
            </div><SENDAFooter /></div>
    );

    return (
        <div className="min-h-screen bg-[#F5F7F8] flex flex-col"><Toaster position="top-right" richColors />
            <header className="bg-white/90 backdrop-blur-xl border-b border-[#DCE3E8]/50 sticky top-0 z-50">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3 sm:gap-4">
                            <Link to="/usuario"><Button variant="ghost" size="icon" className="text-[#3E5F6E]"><ArrowLeft className="h-5 w-5" /></Button></Link>
                            <div>
                                <div className="flex items-center gap-2"><span className="text-xs font-semibold text-[#E8B27A] uppercase tracking-wide">Cap. 5</span><span className="text-xs text-[#7A8A93]">5.5</span></div>
                                <h1 className="text-base sm:text-lg font-semibold text-[#1F2A30] font-['Outfit']">Sistema de ejecución</h1>
                            </div>
                        </div>
                        {savingDraft && <Loader2 className="h-3 w-3 animate-spin text-[#7A8A93]" />}
                    </div>
                </div>
            </header>
            <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 flex-1">
                                {readOnly && <ReadOnlyBanner submittedAt={submittedAt} />}
                <Card className="bg-white border border-[#DCE3E8] rounded-2xl sm:rounded-3xl shadow-sm overflow-hidden">
                    <CardHeader className="bg-gradient-to-r from-[#E8B27A] to-[#D4A06A] p-4 sm:p-6 text-white">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white/20 rounded-xl flex items-center justify-center"><Settings2 className="h-5 w-5 sm:h-6 sm:w-6 text-white" /></div>
                            <div><CardTitle className="text-white text-base sm:text-lg">Integra tu sistema completo</CardTitle><p className="text-xs sm:text-sm text-white/70">Conecta todos los elementos en una secuencia coherente</p></div>
                        </div>
                    </CardHeader>
                    <CardContent className="p-4 sm:p-6 space-y-5">
                        {/* VMV */}
                        <div className="space-y-2 p-4 bg-[#5FAF9D]/5 rounded-xl border border-[#5FAF9D]/20">
                            <Label className="text-sm font-semibold text-[#5FAF9D] flex items-center gap-2"><Minimize2 className="h-4 w-4" />Tu versión mínima viable</Label>
                            <p className="text-xs text-[#7A8A93]">Retoma tu acción mínima del módulo 5.2</p>
                            <Textarea value={vmv} onChange={e => setVmv(e.target.value)} placeholder="Ej: Abrir el documento y escribir una frase sin exigencia de calidad..." className="border-[#5FAF9D]/20 bg-white focus:ring-[#5FAF9D]/20 focus:border-[#5FAF9D]/40 rounded-xl min-h-[70px]" data-testid="input-vmv" />
                        </div>

                        {/* Entorno */}
                        <div className="space-y-2 p-4 bg-[#3E5F6E]/5 rounded-xl border border-[#3E5F6E]/20">
                            <Label className="text-sm font-semibold text-[#3E5F6E] flex items-center gap-2"><Layout className="h-4 w-4" />Tu entorno preparado</Label>
                            <p className="text-xs text-[#7A8A93]">Resume cómo preparas tu espacio (módulo 5.3)</p>
                            <Textarea value={entorno} onChange={e => setEntorno(e.target.value)} placeholder="Ej: Escritorio limpio, teléfono en silencio, documento abierto..." className="border-[#3E5F6E]/20 bg-white focus:ring-[#3E5F6E]/20 focus:border-[#3E5F6E]/40 rounded-xl min-h-[70px]" data-testid="input-entorno" />
                        </div>

                        {/* Disparador */}
                        <div className="space-y-2 p-4 bg-[#E8B27A]/5 rounded-xl border border-[#E8B27A]/20">
                            <Label className="text-sm font-semibold text-[#E8B27A] flex items-center gap-2"><Play className="h-4 w-4" />Tu disparador de arranque</Label>
                            <p className="text-xs text-[#7A8A93]">Tu regla SI-ENTONCES del módulo 5.4</p>
                            <Textarea value={disparador} onChange={e => setDisparador(e.target.value)} placeholder='Ej: SI son las 9:00 y estoy en mi escritorio, ENTONCES abro el documento...' className="border-[#E8B27A]/20 bg-white focus:ring-[#E8B27A]/20 focus:border-[#E8B27A]/40 rounded-xl min-h-[70px]" data-testid="input-disparador" />
                        </div>

                        {/* Secuencia completa */}
                        <div className="space-y-2">
                            <Label className="text-sm font-semibold text-[#3E5F6E]">Secuencia completa: describe tu sistema paso a paso</Label>
                            <p className="text-xs text-[#7A8A93]">Integra todo: preparación del entorno, disparador, acción mínima y cómo continúas</p>
                            <Textarea value={secuencia} onChange={e => setSecuencia(e.target.value)} placeholder='Ej: 1) A las 8:55 preparo mi espacio. 2) A las 9:00 suena la alarma. 3) Abro el documento. 4) Escribo una frase. 5) Si fluye, sigo 10 min más...' className="border-[#DCE3E8] rounded-xl min-h-[100px]" data-testid="input-secuencia" />
                        </div>

                        <div className="flex justify-end pt-4 border-t border-[#DCE3E8]">
                            <Button onClick={handleSubmit} disabled={!canSubmit || loading} className="bg-[#3E5F6E] hover:bg-[#2F4A57] text-white px-6 sm:px-8 py-3 rounded-xl shadow-md" data-testid="submit-sistema-ejecucion">
                                {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Procesando...</> : <><Check className="h-4 w-4 mr-2" />Completar capítulo</>}
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            </main>
            <SENDAFooter />
        </div>
    );
}
